// CourierCostModal — admin per-order courier cost override (deliverable #10)
//
// Opens from the carrier cost cell in OrderTable. Shows the order's costs,
// the lock state, the latest FedEx reconciliation (if any), and the recent
// override history. Lets the admin:
//   • Set a manual override (locks the auto-pipeline)
//   • Pre-fill the override input with the FedEx total (if available)
//   • Clear the manual lock (reverts to max(FedEx, ShipStation))

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Loader2, RotateCcw, Save, Lock, Truck, Info } from 'lucide-react'
import {
  getOrderCourierCostSnapshot,
  setOrderCourierCostOverride,
  clearOrderCourierCostOverride,
} from '../lib/api'
import { ToastContainer, useToast } from './Toast'

function fmt(v, fallback = '—') {
  if (v == null || v === '') return fallback
  const n = Number(v)
  if (!Number.isFinite(n)) return fallback
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(n)
}

function fmtDate(s) {
  if (!s) return '—'
  try { return new Date(s).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' }) }
  catch { return s }
}

export default function CourierCostModal({ orderId, onClose }) {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ['order', orderId, 'courier-cost'],
    queryFn: () => getOrderCourierCostSnapshot(orderId),
    enabled: !!orderId,
  })

  const [overrideValue, setOverrideValue] = useState('')
  const [reason, setReason] = useState('')

  // When the snapshot first loads, seed the input from the current effective cost.
  useEffect(() => {
    if (data && overrideValue === '') {
      const seed = data.effective_courier_cost ?? data.carrier_cost ?? 0
      setOverrideValue(seed.toFixed(2))
    }
  }, [data])  // eslint-disable-line react-hooks/exhaustive-deps

  const saveMut = useMutation({
    mutationFn: () => setOrderCourierCostOverride(orderId, {
      override_cost: parseFloat(overrideValue),
      reason: reason.trim() || null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId, 'courier-cost'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })            // refresh table
      queryClient.invalidateQueries({ queryKey: ['fedex', 'overrides'] }) // refresh audit
      addToast({ type: 'success', message: 'Override saved — order is locked from auto pricing' })
      setReason('')
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Save failed',
    }),
  })

  const clearMut = useMutation({
    mutationFn: () => clearOrderCourierCostOverride(orderId),
    onSuccess: (snapshot) => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId, 'courier-cost'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['fedex', 'overrides'] })
      addToast({ type: 'success', message: 'Manual lock cleared' })
      // Snap the input back to the new effective cost
      if (snapshot) {
        const seed = snapshot.effective_courier_cost ?? snapshot.carrier_cost ?? 0
        setOverrideValue(Number(seed).toFixed(2))
      }
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Clear failed',
    }),
  })

  const isLocked = !!data?.effective_courier_cost_manually_set
  const fedexTotal = data?.fedex_recon?.fedex_shipping_total
  const ssCost     = data?.carrier_cost
  const variance   = data?.fedex_recon?.variance_abs

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
            <div>
              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                Courier Cost Override
                {isLocked && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 inline-flex items-center gap-1">
                    <Lock size={11} /> Manual lock
                  </span>
                )}
              </h3>
              <p className="text-xs text-slate-400 mt-0.5 font-mono">
                {data?.order_number || orderId}
                {data?.tracking_number && (
                  <span className="text-slate-300"> · {data.tracking_number}</span>
                )}
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X size={18} />
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : !data ? (
            <div className="px-6 py-12 text-center text-sm text-slate-500">
              Order not found.
            </div>
          ) : (
            <div className="px-6 py-5 space-y-5">
              {/* Cost summary tiles */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-slate-200 px-3 py-2.5">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">ShipStation</p>
                  <p className="text-lg font-semibold text-slate-700 tabular-nums">{fmt(ssCost)}</p>
                  <p className="text-xs text-slate-400">raw carrier_cost</p>
                </div>
                <div className="rounded-lg border border-slate-200 px-3 py-2.5">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">FedEx</p>
                  <p className="text-lg font-semibold text-slate-700 tabular-nums">
                    {fedexTotal != null ? fmt(fedexTotal) : <span className="text-slate-300 text-sm font-normal">no match</span>}
                  </p>
                  {variance != null && (
                    <p className={`text-xs ${variance > 0 ? 'text-red-500' : variance < 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {variance > 0 ? '+' : ''}{fmt(variance)} variance
                    </p>
                  )}
                </div>
                <div className={`rounded-lg border px-3 py-2.5 ${isLocked ? 'border-purple-200 bg-purple-50/50' : 'border-emerald-200 bg-emerald-50/50'}`}>
                  <p className="text-xs uppercase tracking-wider text-slate-500">Effective</p>
                  <p className="text-lg font-semibold text-slate-800 tabular-nums">{fmt(data.effective_courier_cost)}</p>
                  <p className={`text-xs ${isLocked ? 'text-purple-700' : 'text-emerald-700'}`}>
                    {isLocked ? 'manual' : 'auto'}
                  </p>
                </div>
              </div>

              {/* Breakdown of how the effective cost was computed */}
              {data.effective_courier_cost_breakdown && (
                <div className="rounded-lg bg-slate-50 px-3 py-2.5 border border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                    <Info size={11} />
                    Last computed breakdown
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                    {Object.entries(data.effective_courier_cost_breakdown).map(([k, v]) => (
                      <div key={k} className="flex justify-between font-mono text-slate-600">
                        <span className="text-slate-500">{k}</span>
                        <span>{fmt(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Override form */}
              <form
                onSubmit={(e) => { e.preventDefault(); saveMut.mutate() }}
                className="space-y-3 pt-2 border-t border-slate-200"
              >
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Manual override
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={overrideValue}
                        onChange={(e) => setOverrideValue(e.target.value)}
                        className="w-full pl-7 pr-3 py-1.5 text-sm font-mono tabular-nums border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0.00"
                      />
                    </div>
                    {fedexTotal != null && (
                      <button
                        type="button"
                        onClick={() => setOverrideValue(Number(fedexTotal).toFixed(2))}
                        className="text-xs px-2 py-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 inline-flex items-center gap-1"
                        title="Pre-fill with the FedEx invoice total"
                      >
                        <Truck size={11} /> Use FedEx
                      </button>
                    )}
                    {ssCost != null && (
                      <button
                        type="button"
                        onClick={() => setOverrideValue(Number(ssCost).toFixed(2))}
                        className="text-xs px-2 py-1.5 rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
                        title="Pre-fill with the ShipStation cost"
                      >
                        Use ShipStation
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Reason <span className="text-xs text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. FedEx credited dispute — closing case CSP-12345"
                    className="w-full px-3 py-1.5 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <p className="text-xs text-slate-400 leading-relaxed">
                  Saving locks the effective cost on this order. Future FedEx invoice
                  ingest will leave the value alone. Use <b>Clear manual lock</b> to revert
                  to the automatic max(FedEx, ShipStation) calculation.
                </p>

                <div className="flex justify-between items-center pt-2">
                  {isLocked ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm('Clear the manual lock? The effective cost will revert to max(FedEx, ShipStation).')) {
                          clearMut.mutate()
                        }
                      }}
                      disabled={clearMut.isPending}
                      className="text-amber-600 hover:text-amber-700 text-xs flex items-center gap-1"
                    >
                      {clearMut.isPending ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                      Clear manual lock
                    </button>
                  ) : <div />}
                  <div className="flex gap-2">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50">
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saveMut.isPending || overrideValue === ''}
                      className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                    >
                      {saveMut.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                      Save override
                    </button>
                  </div>
                </div>
              </form>

              {/* History */}
              {data.history && data.history.length > 0 && (
                <div className="pt-3 border-t border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                    Override history (latest {data.history.length})
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {data.history.map((h) => {
                      const delta = (h.override_cost || 0) - (h.original_cost || 0)
                      return (
                        <div key={h.id} className={`text-xs flex items-center gap-2 px-2 py-1.5 rounded ${h.active ? 'bg-slate-50' : 'opacity-60'}`}>
                          <span className="text-slate-500 tabular-nums w-24 flex-shrink-0">{fmtDate(h.set_at)}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] flex-shrink-0 ${h.source === 'manual' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                            {h.source === 'manual' ? 'Manual' : 'FedEx'}
                          </span>
                          <span className="tabular-nums text-slate-600 font-mono">
                            {fmt(h.original_cost)} → {fmt(h.override_cost)}
                          </span>
                          <span className={`tabular-nums text-xs ${delta > 0 ? 'text-red-600' : delta < 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                            ({delta > 0 ? '+' : ''}{fmt(delta)})
                          </span>
                          {h.reason && (
                            <span className="text-slate-500 italic truncate" title={h.reason}>
                              {h.reason}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}
